import { Box, Typography, Paper, Button, IconButton } from '@mui/material';
import Grid from '@mui/material/Grid2';
import { useFormContext, useFieldArray, useWatch, useController } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import Input from '@/components/Common/Input';
import Select from '@/components/Common/Select';
import { RiDeleteBin6Line } from 'react-icons/ri';
import { MdRefresh } from 'react-icons/md';
import { useGetVoiceEngines } from '@/apis/agency/voiceEngines/hook';
import { useGetBusinessPackages } from '@/apis/agency/businessPackages/hook';
import { useGetTelephonyProviders } from '@/apis/agency/telephony/hook';
import { useCreatePromoCode } from '@/apis/agency/businessOnboarding/hooks';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import moment from 'moment';
import LostOnboardingConfirmModal from './LostOnboardingConfirmModal';

const LOST_ONBOARDING_STATUS = 'lost_on_onboarding';

export default function InternalProcessForm() {
  const {
    control,
    setValue,
    formState: { defaultValues }
  } = useFormContext();

  const isStripeIdDisabled = Boolean(defaultValues?.stripe_customer_id);
  const { onboardingFormId } = useParams<{ formId: string; onboardingFormId: string }>();
  const { field: statusField } = useController({ control, name: 'status' });
  const businessLegalName = useWatch({ control, name: 'business_legal_name' }) || '';
  const [showLostOnboardingConfirm, setShowLostOnboardingConfirm] = useState(false);

  const currentStatus = defaultValues?.status || '';
  const STATUS_ORDER = ['agent_preparation', 'agent_qa_testing', 'agent_finalized', 'lost_on_onboarding', 'completed'];
  const isCustomStatus = Boolean(currentStatus && !STATUS_ORDER.includes(currentStatus));
  const isDropdownDisabled = Boolean(
    !currentStatus || ['draft', 'collecting_agent_info', 'awaiting_engineering'].includes(currentStatus)
  );
  const postSetupStatusOptions = [
    { label: 'Agent Preparation', value: 'agent_preparation' },
    { label: 'Agent QA Testing', value: 'agent_qa_testing' },
    { label: 'Agent Finalized', value: 'agent_finalized' },
    { label: 'Lost on Onboarding', value: 'lost_on_onboarding' },
    { label: 'Completed', value: 'completed' }
  ];
  const statusOptions =
    currentStatus === 'in_review'
      ? [{ label: 'Awaiting Engineering', value: 'awaiting_engineering' }]
      : postSetupStatusOptions;

  useEffect(() => {
    if (isCustomStatus) {
      setValue('status', '');
    }
  }, [isCustomStatus, setValue]);

  const handleStatusChange = (event: { target: { value: unknown } }) => {
    const next = String(event.target.value ?? '');
    if (next === statusField.value) return;

    if (next === LOST_ONBOARDING_STATUS) {
      setShowLostOnboardingConfirm(true);
      return;
    }

    statusField.onChange(next);
  };

  const handleConfirmLostOnboarding = () => {
    statusField.onChange(LOST_ONBOARDING_STATUS);
    setShowLostOnboardingConfirm(false);
  };

  const expiresAt = useWatch({ control, name: 'onboarding_coupon_expires_at' });

  const [timeLeft, setTimeLeft] = useState<string>('');
  const [isExpired, setIsExpired] = useState(false);

  const { mutate: resetPromoCode, isPending: isResetting } = useCreatePromoCode();

  useEffect(() => {
    if (!expiresAt) return;

    const calculateTime = () => {
      const now = moment();
      const expires = moment(expiresAt);
      const diff = expires.diff(now);

      if (diff <= 0) {
        setTimeLeft('Expired');
        setIsExpired(true);
        return;
      }

      const duration = moment.duration(diff);
      const days = Math.floor(duration.asDays());
      const hours = duration.hours();
      const minutes = duration.minutes();
      const seconds = duration.seconds();

      let timeString = '';
      if (days > 0) timeString += `${days}d `;
      timeString += `${hours}h ${minutes}m ${seconds}s`;

      setTimeLeft(timeString);
      setIsExpired(false);
    };

    calculateTime();
    const timer = setInterval(calculateTime, 1000);

    return () => clearInterval(timer);
  }, [expiresAt]);

  const handleResetPromoCode = () => {
    const targetId = onboardingFormId;
    if (!targetId) {
      return;
    }

    resetPromoCode(String(targetId), {
      onSuccess: (data: any) => {
        if (data?.onboarding_coupon_code) {
          setValue('onboarding_coupon_code', data.onboarding_coupon_code);
        }
        if (data?.onboarding_coupon_expires_at) {
          setValue('onboarding_coupon_expires_at', data.onboarding_coupon_expires_at);
        }
      }
    });
  };

  const { data: voiceEngines } = useGetVoiceEngines({ per_page: 999 });
  const { data: businessPackages } = useGetBusinessPackages();
  const { data: telephonyProvidersData } = useGetTelephonyProviders();

  const telephonyOptions = useMemo(() => {
    const rows = telephonyProvidersData?.data?.filter((p) => p.active) ?? [];
    return rows.map((p) => ({ label: p.friendly_name, value: String(p.id) }));
  }, [telephonyProvidersData]);

  const externalReferenceSource = useWatch({ control, name: 'external_reference_source' });
  const telephonyProviderIds = useWatch({ control, name: 'telephony_provider_ids' });
  const hasCheckedMatchedProvider = useRef(false);

  useEffect(() => {
    if (!externalReferenceSource || !telephonyProvidersData?.data || hasCheckedMatchedProvider.current) return;

    const matchedProvider = telephonyProvidersData.data.find(
      (p) =>
        p.code?.toLowerCase() === externalReferenceSource.toLowerCase() ||
        p.name?.toLowerCase() === externalReferenceSource.toLowerCase() ||
        p.friendly_name?.toLowerCase() === externalReferenceSource.toLowerCase()
    );

    if (matchedProvider) {
      const matchedIdStr = String(matchedProvider.id);
      const currentIds = Array.isArray(telephonyProviderIds) ? telephonyProviderIds : [];
      if (!currentIds.includes(matchedIdStr)) {
        setValue('telephony_provider_ids', [...currentIds, matchedIdStr]);
      }
      hasCheckedMatchedProvider.current = true;
    }
  }, [externalReferenceSource, telephonyProvidersData, telephonyProviderIds, setValue]);

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'interoffice_notes'
  });

  return (
    <Paper sx={{ border: '1px solid #EAEAEA', borderRadius: 2, p: 4 }}>
      {/* HEADER */}
      <Typography fontWeight={600} fontSize={18}>
        Internal Process
      </Typography>

      <Typography variant='body2' color='text.secondary' mb={3}>
        Configure pricing, engine, and integrations for this agency. Only visible to CSM team.
      </Typography>

      {/* FIELDS */}
      <Grid container spacing={3}>
        <Grid size={{ xs: 12, md: 6 }}>
          <Select
            name='business_plan_id'
            label='Pricing Package'
            control={control}
            required
            sortOrder='none'
            fullWidth
            options={[
              { label: 'Select Package', value: '' },
              ...(businessPackages?.data?.map((pkg) => ({
                label: pkg.name,
                value: String(pkg.id)
              })) || [])
            ]}
          />
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <Select
            name='voice_engine_id'
            label='Engine'
            sortOrder='none'
            control={control}
            required
            fullWidth
            options={[
              { label: 'Select Engine', value: '' },
              ...(voiceEngines?.data
                ?.filter((engine) => !engine.deprecated)
                .map((engine) => ({
                  label: engine.configuration?.friendly_name || engine.code,
                  value: String(engine.id)
                })) || [])
            ]}
          />
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <Input name='coupon_code' label='Coupon Code' control={control} placeholder='e.g. WELCOME20' fullWidth />
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <Input
            name='ticket_reference'
            label='Ticket URL'
            control={control}
            fullWidth
            placeholder='e.g. https://support.external.link'
          />
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <Select
            name='telephony_provider_ids'
            label='Telephony Service'
            control={control}
            sortOrder='none'
            fullWidth
            multiple
            options={telephonyOptions}
            helperText='Active telephony providers for this agency (applies when the business is created).'
          />
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <Select
            name='status'
            label='Onboarding Status'
            control={control}
            sortOrder='none'
            fullWidth
            disabled={isDropdownDisabled}
            options={statusOptions}
            onChange={handleStatusChange}
          />
        </Grid>

        <Grid size={{ xs: 12, sm: 6 }}>
          <Input
            name='external_reference_name'
            label='External Reference Name'
            control={control}
            fullWidth
            slotProps={{
              input: {
                readOnly: true,
                disabled: true
              }
            }}
          />
        </Grid>

        <Grid size={{ xs: 12, sm: 6 }}>
          <Input
            name='external_reference_id'
            label='External Reference ID'
            control={control}
            fullWidth
            slotProps={{
              input: {
                readOnly: true,
                disabled: true
              }
            }}
          />
        </Grid>

        <Grid size={12}>
          <Input
            name='stripe_customer_id'
            label='Stripe Customer ID'
            control={control}
            placeholder='e.g. cus_TveFHxLnKhsWUx'
            helperText='It automatically connect customer account in business billing'
            fullWidth
            slotProps={{
              input: {
                readOnly: isStripeIdDisabled,
                disabled: isStripeIdDisabled
              }
            }}
          />
        </Grid>

        <Grid size={12}>
          <Box mb={3}>
            <Typography fontWeight={600}>Onboarding Coupon</Typography>
            <Typography variant='body2' color='text.secondary'>
              This coupon is generated for the business to use during onboarding.
            </Typography>
          </Box>
          <Box
            display='flex'
            flexDirection={{ xs: 'column', sm: 'row' }}
            gap={2}
            alignItems={{ xs: 'stretch', sm: 'flex-start' }}
          >
            <Box flex={1}>
              <Input
                name='onboarding_coupon_code'
                label='Onboarding Coupon Code'
                control={control}
                placeholder='Generates automatically...'
                fullWidth
                helperText={
                  timeLeft ? (
                    <Typography variant='caption' color={isExpired ? 'error' : 'primary'}>
                      {isExpired ? 'Expired' : `Expires in: ${timeLeft}`}
                    </Typography>
                  ) : null
                }
                slotProps={{
                  input: {
                    readOnly: true,
                    disabled: true
                  }
                }}
              />
            </Box>
            {isExpired && (
              <Button
                variant='outlined'
                onClick={handleResetPromoCode}
                disabled={isResetting}
                startIcon={<MdRefresh />}
                sx={{ width: { xs: '100%', sm: '200px' }, mt: { xs: 0, sm: 1 } }}
              >
                Reset Coupon
              </Button>
            )}
          </Box>
        </Grid>
      </Grid>

      {/* NOTES */}
      <Box mt={3}>
        <Typography fontWeight={600}>Team Notes</Typography>

        <Typography variant='body2' color='text.secondary' mb={2}>
          Internal notes for your team. These notes are not visible to the business.
        </Typography>

        {/* EMPTY STATE */}
        {fields.length === 0 && (
          <Typography color='text.secondary' mb={2}>
            No notes added yet.
          </Typography>
        )}

        {/* LIST */}
        {fields.map((field, index) => (
          <Paper key={field.id} variant='outlined' sx={{ p: 2, mb: 2, position: 'relative' }}>
            <Typography variant='caption' color='text.secondary'>
              Note {index + 1} • {(field as any).timestamp || new Date().toLocaleDateString('en-GB')}
            </Typography>

            <IconButton size='small' onClick={() => remove(index)} sx={{ position: 'absolute', top: 8, right: 8 }}>
              <RiDeleteBin6Line color='#DA0808' />
            </IconButton>

            <Box mt={1}>
              <Input
                name={`interoffice_notes.${index}.note`}
                control={control}
                multiline
                rows={4}
                fullWidth
                placeholder='Enter your note...'
              />
            </Box>
          </Paper>
        ))}

        {/* ADD BUTTON */}
        <Button
          variant='outlined'
          sx={{ borderRadius: 20, textTransform: 'none' }}
          onClick={() =>
            append({
              note: '',
              timestamp: new Date().toLocaleDateString('en-GB')
            })
          }
        >
          + Add Note
        </Button>
      </Box>

      <LostOnboardingConfirmModal
        open={showLostOnboardingConfirm}
        businessName={businessLegalName}
        onClose={() => setShowLostOnboardingConfirm(false)}
        onConfirm={handleConfirmLostOnboarding}
      />
    </Paper>
  );
}
